import { useMemo } from "react";
import {
  Badge,
  Button,
  cn,
  RadioGroup,
  RadioGroupItem,
  ScrollArea,
  ScrollBar,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "web-design-system";
import { MandateStatus } from "creso-core/constants";
import {
  getBankAccountTypeLabel,
  getBankAccountTypeOptions,
} from "creso-core/utils";
import { useMandates, useUpdateInvestorBankDetails } from "creso-core/hooks";
import { PencilIcon, Trash2Icon } from "lucide-react";
import { z } from "zod";
import { useFieldArray } from "react-hook-form";
import { toast } from "sonner";
import { AccountCenterFormSchema } from "./form-schema";
import { handleError } from "../../utils";
import { ConditionalRenderer } from "../primitives";
import { AddEditBankDetailsDialog } from "./bank-details-edit-dialog";
import { Section } from "./section";
import { useAccountForm } from "./context";

export function BankDetailsSection() {
  const { data, isEditing, form, updateChangeSet, client, onChangeSetUpdated } =
    useAccountForm();

  const account = data?.account;

  const { fields, update, remove } = useFieldArray({
    control: form.control,
    name: "bank",
  });

  const { mutateAsync: updateBankDetails } = useUpdateInvestorBankDetails();

  const defaultBankAccountId = useMemo(() => {
    const defaultBank = data.bank?.find((bank) => bank.is_default);
    return defaultBank ? defaultBank.id : data.bank?.[0]?.id;
  }, [data.bank]);

  const addBankAccount = async (values: {
    accountNumber: string;
    ifsc: string;
    type: string;
  }) => {
    try {
      const isDuplicate = fields.reduce((acc, f) => {
        return (
          acc ||
          (f.account_number === values.accountNumber &&
            f.ifsc_code === values.ifsc)
        );
      }, false);

      if (isDuplicate) {
        toast.error("Failed to add bank account", {
          description: "This account number and ifsc code are already in use.",
        });
        return false;
      }

      const bankChangeSet = fields.map((field) => ({
        bank_account_id: field.bank_account_id,
        type: field.type,
        account_number: field.account_number,
        ifsc_code: field.ifsc_code,
        primary_account_holder_name: field.primary_account_holder_name,
      }));
      await updateChangeSet({
        INVESTOR_BANK_ACCOUNT: [
          ...bankChangeSet,
          {
            type: values.type,
            account_number: values.accountNumber,
            ifsc_code: values.ifsc,
            primary_account_holder_name: data?.profile?.name ?? "",
          },
        ],
      });
      if (client.status === "ACTIVE") {
        onChangeSetUpdated?.();
      }
      return true;
    } catch (error) {
      handleError(error, "Failed to add bank account");
      return false;
    }
  };

  const updateDefaultBankAccount = async (value: string) => {
    const bankAccountId = parseInt(value, 10);
    const bank = data.bank?.find((bank) => bank.id === bankAccountId);
    if (!bank) return;
    await updateBankDetails({
      investment_account_guid: account?.id ?? "",
      bank_account_guid: bank.bank_account_guid,
      is_default: true,
    }).catch((error) => {
      handleError(error, "Failed to update default bank account");
    });
  };

  const updateBankAccount = async (
    values: {
      accountNumber: string;
      ifsc: string;
      type: string;
    },
    index: number
  ) => {
    let isDuplicate = false;
    fields.forEach((f, i) => {
      if (i === index) return;
      if (
        f.account_number === values.accountNumber &&
        values.ifsc === f.ifsc_code
      ) {
        isDuplicate = true;
      }
    });

    if (isDuplicate) {
      toast.error("Failed to update bank account", {
        description: "This account number and ifsc code are already in use.",
      });
      return false;
    }

    update(index, {
      ...fields[index],
      type: values.type,
      account_number: values.accountNumber,
      ifsc_code: values.ifsc,
    });
    return true;
  };

  return (
    <Section title="Bank details">
      <div className="flex flex-col gap-4">
        <ScrollArea>
          <ScrollBar orientation="horizontal" />
          <RadioGroup
            key={defaultBankAccountId?.toString()}
            className="flex flex-row gap-4"
            defaultValue={defaultBankAccountId?.toString()}
            onValueChange={updateDefaultBankAccount}
          >
            {fields?.map((b, index) => (
              <BankAccountCard
                key={b?.id}
                bankAcc={b}
                investmentAccountId={account?.id ?? ""}
                onUpdate={(values) => {
                  return updateBankAccount(values, index);
                }}
                onDelete={() => {
                  remove(index);
                }}
              />
            ))}
          </RadioGroup>
        </ScrollArea>
        <AddEditBankDetailsDialog
          investmentAccountId={account?.id ?? ""}
          data={{
            type: getBankAccountTypeOptions(client)[0].value,
            primary_account_holder_name:
              data?.profile?.name ??
              fields?.[0]?.primary_account_holder_name ??
              "",
            account_number: "",
            is_default: false,
          }}
          onUpdate={addBankAccount}
          trigger={
            <Button
              variant="link"
              className={cn("hidden px-0 w-fit", !isEditing && "flex")}
              type="button"
            >
              + Add bank account
            </Button>
          }
        />
      </div>
    </Section>
  );
}

function BankAccountCard({
  investmentAccountId,
  bankAcc,
  onUpdate,
  onDelete,
}: {
  bankAcc: z.infer<typeof AccountCenterFormSchema>["bank"][number];
  investmentAccountId: string;
  onUpdate: (values: {
    accountNumber: string;
    ifsc: string;
    type: string;
  }) => Promise<boolean> | boolean;
  onDelete: () => void;
}) {
  const { mandates } = useMandates(
    investmentAccountId,
    !!bankAcc.bank_account_id
  );

  const { isEditing, data } = useAccountForm();

  const activeMandatesCount = useMemo(
    () =>
      (mandates || []).filter(
        (m) =>
          (m.status === MandateStatus.ACTIVE ||
            m.status === MandateStatus.APPROVED ||
            m.status === MandateStatus.UNDER_PROCESSING) &&
          m.bank_account_id === bankAcc.bank_account_id
      ).length,
    [mandates, bankAcc.bank_account_id]
  );

  const maskedAccountNumber = `*${bankAcc.account_number?.slice(-4)}`;

  const accountType = getBankAccountTypeLabel(bankAcc.type);

  const savedBankAccount = data.bank?.find(
    (b) => b.id === bankAcc.bank_account_id
  );

  return (
    <div className="flex items-start text-primary gap-3 w-90 p-4 relative rounded-lg border border-border">
      <RadioGroupItem
        className="mt-0.5"
        value={bankAcc.bank_account_id?.toString() ?? "new"}
        disabled={!bankAcc.bank_account_id}
      />

      <div className="flex flex-col flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">
            {savedBankAccount?.bank_name ?? "New bank account"}
          </span>
          <ConditionalRenderer condition={!!savedBankAccount?.is_default}>
            <Badge
              variant="custom"
              className="text-xs flex items-center text-tag-blue-foreground bg-tag-blue"
            >
              Default
            </Badge>
          </ConditionalRenderer>
          <ConditionalRenderer condition={!savedBankAccount}>
            <Badge
              variant="custom"
              className="text-xs text-tag-green-foreground bg-tag-green"
            >
              New
            </Badge>
          </ConditionalRenderer>
          <ConditionalRenderer
            condition={savedBankAccount?.verification_status === "unverified"}
          >
            <Badge
              variant="custom"
              className="text-xs text-tag-yellow-foreground bg-tag-yellow"
            >
              Awaiting review
            </Badge>
          </ConditionalRenderer>
          <ConditionalRenderer
            condition={savedBankAccount?.verification_status === "failed"}
          >
            <Badge
              variant="custom"
              className="text-xs text-tag-red-foreground bg-tag-red"
            >
              Failed
            </Badge>
          </ConditionalRenderer>
        </div>
        <div className="text-sm mt-2 font-semibold">
          {accountType} {maskedAccountNumber}
        </div>
        <div className="flex flex-row items-baseline mt-1 justify-between">
          <span className="text-sm text-muted-foreground">
            {bankAcc.ifsc_code}
          </span>
          {bankAcc.bank_account_id && activeMandatesCount > 0 && (
            <span className="text-sm font-semibold">
              {activeMandatesCount} mandate(s)
            </span>
          )}
        </div>
      </div>

      <ConditionalRenderer condition={isEditing}>
        <div className="absolute top-2 right-2 flex flex-row gap-1 items-center">
          <ConditionalRenderer condition={activeMandatesCount > 0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className={cn(
                    "invisible",
                    isEditing && "visible",
                    "opacity-50 hover:bg-transparent cursor-not-allowed"
                  )}
                  type="button"
                >
                  <PencilIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Account has mandate in use</TooltipContent>
            </Tooltip>
          </ConditionalRenderer>
          <ConditionalRenderer condition={activeMandatesCount === 0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <AddEditBankDetailsDialog
                  investmentAccountId={investmentAccountId}
                  data={bankAcc}
                  trigger={
                    <Button
                      size="icon"
                      variant="ghost"
                      className={cn("invisible", isEditing && "visible")}
                      type="button"
                    >
                      <PencilIcon />
                    </Button>
                  }
                  onUpdate={onUpdate}
                />
              </TooltipTrigger>
              <TooltipContent>Edit</TooltipContent>
            </Tooltip>
          </ConditionalRenderer>
          <ConditionalRenderer condition={!bankAcc.bank_account_id}>
            <Button onClick={onDelete} variant="ghost" size="icon">
              <Trash2Icon />
            </Button>
          </ConditionalRenderer>
        </div>
      </ConditionalRenderer>
    </div>
  );
}
